import React, { useState, useEffect } from "react";
import { useDataProvider, useRedirect } from "react-admin";
import {
  Box,
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import DeleteDialog from "./dialogs/DeleteHourlyBonusWheel";
import { Loader } from "../Loader";

export const HourlyBonusWheel = () => {
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const navigate = useNavigate();

  const rewardTypeNames = {
    1: "Crowns",
    2: "Gems",
    3: "Good Luck",
  };

  useEffect(() => {
    fetchConfiguration();
  }, []);

  const fetchConfiguration = async () => {
    setLoading(true);
    try {
      const response = await dataProvider.getList("HourlyBonusWheel", {
        pagination: { page: 1, perPage: 1 },
        sort: { field: "createdAt", order: "DESC" },
        filter: {},
      });
      if (response.data && response.data.length > 0) {
        setRecord(response.data[0]);
      } else {
        setRecord(null);
      }
    } catch (error) {
      console.error("Error fetching configuration:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate("/edithourlybonuswheel", { state: { record } });
  };

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleDeleteClose = () => {
    setOpenDeleteDialog(false);
    fetchConfiguration(); // Refresh after delete
  };

  if (loading) {
    return <Loader />;
  }

  // If no configuration exists, show create button
  if (!record) {
    return (
      <Box sx={{ mt: 4, p: 3 }}>
        <Typography variant="h5" sx={{ mb: 3, color: "#FFC107" }}>
          Hourly Bonus Wheel Configuration
        </Typography>
        <Card sx={{ backgroundColor: "#242424", p: 3 }}>
          <CardContent>
            <Typography variant="body1" sx={{ mb: 3, textAlign: "center" }}>
              No configuration found. Please create one.
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => redirect("/createhourlybonuswheel")}
                sx={{
                  background: "#FFC107",
                  color: "#0A0A0A",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#FFA000",
                  },
                }}
              >
                Add Configuration
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 4, p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" sx={{ color: "#FFC107" }}>
          Hourly Bonus Wheel Configuration
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{
              background: "#FFC107",
              color: "#0A0A0A",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#FFA000",
              },
            }}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
            sx={{
              borderColor: "#FF5252",
              color: "#FF5252",
              textTransform: "none",
              "&:hover": {
                borderColor: "#FF1744",
                backgroundColor: "rgba(255, 82, 82, 0.1)",
              },
            }}
          >
            Delete
          </Button>
        </Box>
      </Box>

      <Card sx={{ backgroundColor: "#242424" }}>
        <CardContent>
          <Grid container spacing={3}>
            {/* Main Configuration Fields */}
            <Grid item xs={12} sm={6} md={4}>
              <Box>
                <Typography variant="caption" sx={{ color: "#999", display: "block", mb: 0.5 }}>
                  Cooldown (Hours)
                </Typography>
                <Typography variant="body1" sx={{ color: "#fff", fontWeight: 500 }}>
                  {record.cooldownHours || "N/A"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box>
                <Typography variant="caption" sx={{ color: "#999", display: "block", mb: 0.5 }}>
                  Minimum VC Per Reward
                </Typography>
                <Typography variant="body1" sx={{ color: "#fff", fontWeight: 500 }}>
                  {record.minValue || "N/A"}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <Box>
                <Typography variant="caption" sx={{ color: "#999", display: "block", mb: 0.5 }}>
                  Maximum VC Per Reward
                </Typography>
                <Typography variant="body1" sx={{ color: "#fff", fontWeight: 500 }}>
                  {record.maxValue || "N/A"}
                </Typography>
              </Box>
            </Grid>

            {/* Divider */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2, borderColor: "#FFC107" }} />
              <Typography variant="h6" sx={{ color: "#FFC107", mb: 2 }}>
                Slots Configuration
              </Typography>
            </Grid>

            {/* Slots Display */}
            {record.slots && Array.isArray(record.slots) && record.slots.length > 0 ? (
              record.slots.map((slot, index) => (
                <Grid item xs={12} key={`slot-${index}`}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="caption" sx={{ color: "#999", display: "block", mb: 0.5 }}>
                          Slot {index + 1} - Reward Type
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#fff", fontWeight: 500 }}>
                          {rewardTypeNames[slot.Type] || `Type ${slot.Type}`}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography variant="caption" sx={{ color: "#999", display: "block", mb: 0.5 }}>
                          Slot {index + 1} - Amount
                        </Typography>
                        <Typography variant="body1" sx={{ color: "#fff", fontWeight: 500 }}>
                          {slot.Amount || 0}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: "#999" }}>
                  No slots configured
                </Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <DeleteDialog
        open={openDeleteDialog}
        onClose={handleDeleteClose}
        record={record}
      />
    </Box>
  );
};
